import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/dashboard/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Course } from "@/types/course";
import { Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { devLog, devError } from "@/utils/devLog";

const CourseCard = ({ course }: { course: Course }) => {
  const thumbnailUrl = course.thumbnail_url || "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fHdlYiUyMGRldmVsb3BtZW50fGVufDB8fDB8fHww";
  
  const [username, setUsername] = useState<string | null>(null);
  
  useEffect(() => {
    const getUsername = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data, error } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', session.user.id)
            .single();
          
          if (error) {
            console.error("Error fetching username:", error);
            return;
          }
          
          setUsername(data?.username);
        }
      } catch (err) {
        console.error("Error in getUsername:", err);
      }
    };
    
    getUsername();
  }, []);
  
  // Create the public course URL with origin to ensure it's an absolute path
  const publicCourseUrl = username && course.slug 
    ? `/${username}/${course.slug}`
    : null;
  
  const handleCopyUrl = async () => {
    if (!course.published) {
      toast.error("Course must be published before sharing");
      return;
    }
    
    if (!username || !course.slug) {
      toast.error("Course URL not available");
      return;
    }
    
    const url = `${window.location.origin}/${username}/${course.slug}`;
    await navigator.clipboard.writeText(url);
    toast.success("Course URL copied to clipboard");
  };
  
  return (
    <Card className="overflow-hidden">
      <div className="aspect-video w-full overflow-hidden">
        <img 
          src={thumbnailUrl} 
          alt={course.title}
          className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
        />
      </div>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg mb-1">{course.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
          </div>
          <Badge variant={course.published ? "default" : "secondary"}>
            {course.published ? "Published" : "Draft"}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-muted-foreground">Price</p>
            <p className="font-medium">{course.price ? `$${course.price}` : 'Free'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Created</p>
            <p className="font-medium">{new Date(course.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Link to={`/dashboard/courses/${course.id}/edit`} className="flex-1">
            <Button variant="outline" className="w-full">
              Edit
            </Button>
          </Link>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleCopyUrl}
            title={!course.published ? "Course must be published first" : "Copy course URL"}
          >
            <Copy className="h-4 w-4" />
          </Button>
          
          {publicCourseUrl && course.published ? (
            <Link 
              to={publicCourseUrl} 
              className="flex-1"
            >
              <Button className="w-full">View</Button>
            </Link>
          ) : (
            <Button 
              className="flex-1 w-full" 
              disabled={!course.published || !course.slug} 
              title={!course.published ? "Course must be published first" : !course.slug ? "Course must have a slug/URL" : ""}
            >
              View
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Courses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchCourses = async () => {
    setIsLoading(true);
    setFetchError(null);
    
    try {
      devLog("[Courses] Checking current session");
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setFetchError("You must be logged in to view courses");
        setIsLoading(false);
        return;
      }
      
      devLog("[Courses] Fetching courses for user:", session.user.id);
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('creator_id', session.user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        devError('[Courses] Error fetching courses:', error);
        setFetchError("Failed to load courses: " + error.message);
        toast.error("Failed to load courses");
        return;
      }
      
      devLog('[Courses] Successfully fetched courses:', data?.length || 0);
      
      const coursesWithPublished = data?.map(course => ({
        ...course,
        published: course.published ?? false
      })) as Course[];
      
      setCourses(coursesWithPublished || []);
    } catch (error: any) {
      devError('[Courses] Unexpected error fetching courses:', error);
      setFetchError("An unexpected error occurred while loading courses");
      toast.error("Failed to load courses");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCourses();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        fetchCourses();
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  const handleRetryFetch = () => {
    fetchCourses();
  };
  
  if (!isLoading && courses.length === 0 && !fetchError) {
    return (
      <DashboardLayout 
        title="Courses" 
        description="Create and manage your course content"
        actions={
          <Link to="/dashboard/courses/new">
            <Button>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="12" x2="12" y1="5" y2="19"></line>
                <line x1="5" x2="19" y1="12" y2="12"></line>
              </svg>
              New Course
            </Button>
          </Link>
        }
      >
        <Card className="border-dashed border-2">
          <CardContent className="p-6 flex flex-col items-center justify-center text-center py-10">
            <div className="rounded-full bg-primary/10 p-6 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">No courses yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Create your first course to start sharing your knowledge with the world.
            </p>
            <Link to="/dashboard/courses/new">
              <Button size="lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <line x1="12" x2="12" y1="5" y2="19"></line>
                  <line x1="5" x2="19" y1="12" y2="12"></line>
                </svg>
                Create Your First Course
              </Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }
  
  if (!isLoading && fetchError) {
    return (
      <DashboardLayout 
        title="Courses" 
        description="Create and manage your course content"
        actions={
          <Link to="/dashboard/courses/new">
            <Button>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="12" x2="12" y1="5" y2="19"></line>
                <line x1="5" x2="19" y1="12" y2="12"></line>
              </svg>
              New Course
            </Button>
          </Link>
        }
      >
        <div className="flex flex-col items-center justify-center py-16">
          <div className="bg-destructive/10 text-destructive p-4 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">Could not load courses</h3>
          <p className="text-muted-foreground text-center max-w-md mb-6">{fetchError}</p>
          <Button onClick={handleRetryFetch}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
              <path d="M3 22v-6h6"></path>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
            </svg>
            Try Again
          </Button>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout 
      title="Courses" 
      description="Create and manage your course content"
      actions={
        <Link to="/dashboard/courses/new">
          <Button>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <line x1="12" x2="12" y1="5" y2="19"></line>
              <line x1="5" x2="19" y1="12" y2="12"></line>
            </svg>
            New Course
          </Button>
        </Link>
      }
    >
      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map(course => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
          
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="bg-primary/10 rounded-lg p-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 8v4"></path>
                    <path d="M12 16h.01"></path>
                  </svg>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-medium mb-1">Course Creation Tips</h3>
                  <p className="text-muted-foreground mb-4">
                    Creating engaging courses that convert requires careful planning and execution.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/help/course-creation">
                      <Button variant="outline" size="sm">
                        Course Creation Guide
                      </Button>
                    </Link>
                    <Link to="/help/pricing-strategy">
                      <Button variant="outline" size="sm">
                        Pricing Strategy
                      </Button>
                    </Link>
                    <Link to="/help/content-tips">
                      <Button variant="outline" size="sm">
                        Content Tips
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </DashboardLayout>
  );
};

export default Courses;
